// SP-2 WS-7 — confirm-and-explain modal for generating the month's
// statement. Explains what generating does before the click and, on
// success, says what happened + who holds the baton next.

import { useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useGenerateStatement } from "@/hooks/portfolioAdmin/useMonthlyCycle";
import { monthLabel } from "./cycleCopy";

export function GenerateStatementModal({
  loanId,
  period,
  periodStart,
  periodEnd,
  open,
  onClose,
}: {
  loanId: string;
  period: string;
  periodStart: string;
  periodEnd: string;
  open: boolean;
  onClose: () => void;
}) {
  const mutation = useGenerateStatement(loanId);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const month = monthLabel(period);

  const confirm = async () => {
    setError(null);
    try {
      await mutation.mutateAsync({
        periodStart: periodStart.slice(0, 10),
        periodEnd: periodEnd.slice(0, 10),
      });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "The statement could not be generated.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Generate the ${month} statement`}
      footer={
        done ? (
          <Button onClick={onClose} data-testid="generate-done">
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => void confirm()}
              loading={mutation.isPending}
              data-testid="generate-confirm"
            >
              Generate statement
            </Button>
          </>
        )
      }
    >
      {done ? (
        <p className="text-body text-onyx m-0" data-testid="generate-success">
          Statement created — it&apos;s now waiting for your review.
        </p>
      ) : (
        <div className="flex flex-col gap-space-3" data-testid="generate-modal">
          <p className="text-body text-onyx m-0">
            This creates the {month} bill from the loan&apos;s real schedule —
            this month&apos;s principal &amp; interest plus the servicing fee.
            Nothing is sent yet: the statement comes back to you for review
            before it goes to the servicer.
          </p>
          {error && (
            <p className="text-small text-status-error m-0" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
